import { useGetElementDetail } from '@vindr/services/actions/element.action';
import useRefreshComponent from '@vindr/utils/hooks/useRefreshComponent';
import useToggle from '@vindr/utils/hooks/useToggle';
import { Checkbox, Form, Input, Select } from 'antd';
import isEqual from 'lodash/isEqual';
import { Fragment, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InputComponentElement from '../../../InputComponentElement';
import handleArrowKeyNavigation from '../../handleArrowKeyNavigation';
import { StructuredReportElement } from '../types';
import SelectionElement from './SelectionElement';

export interface SelectionProps {
  disabled: boolean;
  onChange: (
    val: string | number,
    element: StructuredReportElement,
    preventHighlight?: boolean // default false
  ) => void;
  element: SelectionElement;
  index?: number;
}

const Selection = ({ disabled, onChange, element, index }: SelectionProps) => {
  const { t } = useTranslation('Vindoc');

  const { value: open, setFalse: setClose, toggle } = useToggle();
  const [refresh, setRefresh] = useRefreshComponent();
  const [freeTextValue, setFreeTextValue] = useState(element.free_text_value);
  const [isHidden, setIsHidden] = useState(element.is_hidden);

  useEffect(() => {
    element.setIsHidden(isHidden);
    onChange(element.value, element);
    setRefresh();
  }, [element, isHidden, onChange, setRefresh]);

  const { data, isLoading } = useGetElementDetail(
    { id: element.id },
    { enabled: !!element.linked_element_ids?.length && element.elements.length === 0 }
  );

  useEffect(() => {
    if (element?.free_text_value !== freeTextValue) {
      setFreeTextValue(element.free_text_value);
    }
    if (isLoading || !data?.elements?.length || element.elements.length > 0) {
      // element.loadElements();
      return;
    }
    const mergeElements = data.elements.map((x, index) => ({
      ...x,
      ...element.elements[index],
    }));
    onChange(element.value, element, true);
    element.setElements(mergeElements);
    setRefresh();
  }, [data, element, isLoading, setRefresh]);

  const handleSelectChange = useCallback(
    (value: string | number) => {
      onChange(value, element);
    },
    [element, onChange]
  );

  const selectedElement: StructuredReportElement = useMemo(() => {
    if (!element.isLinkedSelect) return null;
    return element.elements.find((it) => it?.id === element?.value);
  }, [element?.elements, element.value]);

  return (
    <Fragment key={refresh}>
      <div key={element.id} className="flex items-start">
        <Checkbox
          checked={!isHidden}
          className="mr-2 mt-0.5"
          onChange={(e) => setIsHidden(!e.target.checked)}
        />
        <Form.Item
          className="mb-3 flex-1"
          tooltip={element.tooltip ? { title: element.tooltip } : undefined}
          key={[element?.parent?.name, element.level, element.id].join('|')}
          // label={`${element.label}${index ? ' ' + index : ''}`}
          label={[element.name, index ?? ''].join(' ')}
        >
          <Select
            allowClear
            disabled={disabled}
            dropdownMatchSelectWidth={false}
            getPopupContainer={(trigger) => trigger.parentElement} // make the popup list fix position with parent on scroll
            open={open}
            optionLabelProp="label"
            placeholder={t(element.placeholder || 'Select an option')}
            value={element.value}
            onBlur={setClose}
            onChange={handleSelectChange}
            onClick={toggle}
            onKeyDown={(event) => handleArrowKeyNavigation({ event, toggle, open })}
          >
            {element.items.map((item, index) => (
              <Select.Option
                key={`${item.value}|${index}`}
                label={item.value === 'FREE_TEXT_VALUE' ? t('Free Text') : item.label || item.value}
                value={item.value}
              >
                {item.value === 'FREE_TEXT_VALUE' ? t('Free Text') : item.label || item.value}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </div>
      {selectedElement && (
        <div key={refresh} style={{ paddingLeft: 8 }}>
          <InputComponentElement
            element={selectedElement}
            onChange={onChange}
            disabled={disabled}
            // {...selected.element}
          />
        </div>
      )}
      {element.value === 'FREE_TEXT_VALUE' ? (
        <Form.Item key={element?.id} className="mb-3" label={t('Free Text')}>
          <Input
            allowClear
            disabled={disabled}
            placeholder={t('Input details')}
            value={element?.free_text_value}
            onKeyDown={(event) => handleArrowKeyNavigation({ event, open: true })}
            onChange={(e) => {
              element.free_text_value = e.target.value;
              setFreeTextValue(element.free_text_value);
              onChange(element.value, element);
            }}
          />
        </Form.Item>
      ) : null}
    </Fragment>
  );
};

export default memo(
  Selection,
  (prev, next) => isEqual(prev.element, next.element) && prev.disabled === next.disabled
);
